/**
 * The venue-cache auditor's vocabulary and the readers for one finding.
 *
 * `@/types/tina` owns the wire shapes (`VenueAuditFindingRow`, `VenueAuditEvidence`,
 * `VenueAuditRunResponse`); this module owns everything the curator surface needs on top
 * of them — the labels, the per-kind hint a curator applies 123 times in one sitting, the
 * one-line summary a row is judged from, and the two deep links out of a finding. Same
 * split, and the same `Record<Union, string>` discipline, as `@/lib/tina/refresh-jobs`:
 * adding a member to `VENUE_AUDIT_KINDS` without labelling it here is a compile error
 * rather than a silently blank cell.
 *
 * **Why the summary names venues AND addresses.** For a `cid_collision` the distance alone
 * cannot separate "one club, renamed" from "two different bars": in the real data, pairs
 * at 24 m and 45 m are genuinely different businesses while pairs at 0 m are the same
 * place under two spellings. So the names and the addresses are the evidence and the
 * distance is the hint, not the other way round.
 *
 * Every render path degrades instead of throwing. `kind` is typed as a union but arrives
 * as wire data from an orchestrator deploy that may be newer than this build, so an
 * unknown kind falls back to the fingerprint — a new server-side check must never blank
 * the page.
 */
import {
  VENUE_AUDIT_KINDS,
  VENUE_AUDIT_SEVERITIES,
  VENUE_AUDIT_STATUSES,
  type VenueAuditEvidenceVenue,
  type VenueAuditFindingRow,
  type VenueAuditKind,
  type VenueAuditSeverity,
  type VenueAuditStatus,
} from "@/types/tina";

/** Short label for a kind — what the "What" column and the count strip show. */
export const VENUE_AUDIT_KIND_LABEL: Record<VenueAuditKind, string> = {
  placeholder_fan_in: "Placeholder catch-all",
  cid_collision: "CID on two venues",
  cid_self_inconsistent: "CID contradicts own place id",
  merged_cid_overlap: "Merged CID owned elsewhere",
  non_venue_type: "Not a venue type",
  venue_missing: "Venue no longer live",
  cid_drift: "CID drifted off venue",
  venue_named_by_address: "Named after its own address",
  duplicate_name_and_address: "Same name, same address",
  duplicate_google_place: "One Google place, two records",
  place_not_venue: "A place, not a venue",
  venue_twin: "Same event at two venues",
};

/**
 * What the curator is being asked to judge, per kind.
 *
 * Shown once above the table rather than per row, because the first real backlog is ~178
 * findings of which ~123 are a single kind: the question is read once and answered 123
 * times, so it belongs beside the filter that selects the kind, not repeated in every row.
 */
export const VENUE_AUDIT_KIND_HINT: Record<VenueAuditKind, string> = {
  placeholder_fan_in:
    "Several unrelated placeholder spellings collapsed onto one venue, so every event on it inherited a venue nobody chose for it. Dismiss only if that venue really is the right home for all of them.",
  cid_collision:
    "One Google CID is claimed by two venue records. Read both names and both addresses, not just the distance: 0 m apart is usually one place spelled twice, while 24 m apart can be two genuinely different businesses.",
  cid_self_inconsistent:
    "The venue's stored CID is not the CID its own Google place id encodes, so one document names two different places. One of the two values is wrong.",
  merged_cid_overlap:
    "This venue merged in a CID that another live venue still holds as its primary, so the two documents disagree about who owns the place.",
  non_venue_type:
    "Events are bound to a record whose Google type names a place rather than a venue — a city, a neighbourhood, an attraction. Judge whether it is a real venue Google typed oddly, or a map pin nothing should be bound to.",
  venue_missing:
    "Cache rows still point at a venue that is deleted, hidden or never registered, so every event on it shows a reference users cannot open.",
  cid_drift:
    "The cache's Google CID is no longer this venue's — not as its primary CID, not as a merged one. Whatever evidence justified the bind is gone; check whether the venue moved CIDs or the bind was wrong from the start.",
  venue_named_by_address:
    "Google returned a bare street address rather than a business, and a venue was created and named after it. Judge whether a real business belongs at that address or the record should go.",
  duplicate_name_and_address:
    "Two or more live venues share the exact same name and street — one real place, split into two records. Low severity: an event on either record already lands on the right building. Pick which one survives and merge the rest into it.",
  duplicate_google_place:
    "One venue's own Google place id decodes to the CID another venue claims as its primary, so the two records provably name a single Google place. No names were compared to reach that — it is arithmetic on two identifiers. Pick which record survives and merge.",
  place_not_venue:
    "The venue's Google type names a place rather than a business — a school, a square, a beach, a sports field — and it has held at most one event ever. Usually a village festival that needed somewhere to point. Judge whether it deserves a venue page, or whether the events should carry a map pin instead.",
  venue_twin:
    "Two live venues within 150 m each published the same event on the same night, so users see the concert twice. Open both events and judge: one place held as two records means merge the venues and delete the duplicate events; a room inside its building is two real venues, so delete the duplicate events and dismiss; one event bound to the wrong nearby venue means fix that event's venue. High severity is within 50 m, low is 50 to 150 m.",
};

/** Kind label that survives a kind this build does not know. */
export const venueAuditKindLabel = (kind: string): string =>
  kind in VENUE_AUDIT_KIND_LABEL
    ? VENUE_AUDIT_KIND_LABEL[kind as VenueAuditKind]
    : kind;

export const VENUE_AUDIT_KIND_OPTIONS: {
  value: VenueAuditKind;
  label: string;
}[] = VENUE_AUDIT_KINDS.map((kind) => ({
  value: kind,
  label: VENUE_AUDIT_KIND_LABEL[kind],
}));

export const VENUE_AUDIT_STATUS_LABEL: Record<VenueAuditStatus, string> = {
  open: "Open",
  dismissed: "Dismissed",
  resolved: "Fixed itself",
  all: "Everything",
};

export const VENUE_AUDIT_STATUS_OPTIONS: {
  value: VenueAuditStatus;
  label: string;
}[] = VENUE_AUDIT_STATUSES.map((status) => ({
  value: status,
  label: VENUE_AUDIT_STATUS_LABEL[status],
}));

export const VENUE_AUDIT_SEVERITY_LABEL: Record<VenueAuditSeverity, string> = {
  high: "High",
  low: "Low",
};

export const VENUE_AUDIT_SEVERITY_OPTIONS: {
  value: VenueAuditSeverity;
  label: string;
}[] = VENUE_AUDIT_SEVERITIES.map((severity) => ({
  value: severity,
  label: VENUE_AUDIT_SEVERITY_LABEL[severity],
}));

/**
 * Narrowed on read rather than trusted, exactly as `/tina/refresh-review` narrows its
 * persisted mode: these values come back from `localStorage`, where a kind this build no
 * longer knows can be sitting, and the route types `kind`, `severity` and `status` as
 * Literals — an unknown value is a 422, not an empty page.
 */
export const isVenueAuditKind = (
  value: string | null,
): value is VenueAuditKind =>
  value !== null && VENUE_AUDIT_KINDS.includes(value as VenueAuditKind);

export const isVenueAuditSeverity = (
  value: string | null,
): value is VenueAuditSeverity =>
  value !== null &&
  VENUE_AUDIT_SEVERITIES.includes(value as VenueAuditSeverity);

export const isVenueAuditStatus = (
  value: string | null,
): value is VenueAuditStatus =>
  value !== null && VENUE_AUDIT_STATUSES.includes(value as VenueAuditStatus);

/** Where a curator goes to look at the venue itself. */
export const venueAuditVenueHref = (venueId: string): string =>
  `/venues/edit/${venueId}`;

/**
 * The place on Google Maps, from the CID the finding is keyed on.
 *
 * Same form the venue-resolution HITL panel already links, and the fastest way to settle
 * a collision: it opens the ONE place both venue records claim.
 */
export const venueAuditCidHref = (
  cid: string | null | undefined,
): string | null => (cid ? `https://maps.google.com/?cid=${cid}` : null);

const metres = (value: number | null | undefined): string =>
  value == null ? "distance unknown" : `${Math.round(value)} m apart`;

/**
 * The name to lead one venue with, shared by the grid's Finding column and
 * `summariseFinding`'s dismiss-dialog text so the two never disagree at the point a
 * curator commits to a permanent action.
 *
 * `evidence_venue_for` blanks `name`/`address` deliberately once a venue is gone from the
 * catalog rather than raising, so a `venue_missing` venue's own name is usually `""` — but
 * the source page that fed the cache still called it something, and that survives in
 * `evidence.name_variants`. Lead with that instead, quoted so it reads as the spelling a
 * source used rather than a name Tina confirmed. Falls back to the bare venue id only when
 * neither is available; a venue hidden rather than deleted still has its own name, which
 * always wins.
 */
export const venueAuditVenueDisplayName = (
  venue: VenueAuditEvidenceVenue,
  row: VenueAuditFindingRow,
): string => {
  if (venue.name) return venue.name;
  const extracted =
    row.kind === "venue_missing"
      ? (row.evidence.name_variants ?? [])[0]
      : undefined;
  return extracted ? `"${extracted}"` : venue.venue_id;
};

/** `name — address`, falling back through a blank name to the raw id. */
export const venueAuditVenueLine = (
  venue: VenueAuditEvidenceVenue,
  row: VenueAuditFindingRow,
): string => {
  const name = venueAuditVenueDisplayName(venue, row);
  return venue.address ? `${name} — ${venue.address}` : name;
};

/** Every venue in the finding, named and addressed, in the order the audit stored them. */
export const venueAuditVenuesText = (row: VenueAuditFindingRow): string =>
  (row.evidence.venues ?? [])
    .map((venue) => venueAuditVenueLine(venue, row))
    .join("  ·  ");

/**
 * The kind-specific half of the summary: the one number or contradiction that makes this
 * finding a finding.
 *
 * Returns `""` for a kind this build does not know, which is what lets `summariseFinding`
 * fall through to the fingerprint instead of rendering a hole.
 */
export const venueAuditFindingMetric = (row: VenueAuditFindingRow): string => {
  const evidence = row.evidence;
  switch (row.kind) {
    case "cid_collision":
    case "merged_cid_overlap": {
      const human = evidence.human_confirmed_disagreement
        ? "  ·  a human chose differently for this CID"
        : "";
      return `${metres(evidence.max_distance_m)}${human}`;
    }
    case "placeholder_fan_in":
      return `${evidence.distinct_cache_keys ?? 0} placeholder spellings across ${evidence.distinct_sites ?? 0} sites`;
    case "cid_self_inconsistent":
      return `stores ${evidence.stored_cid ?? "?"} but its own place id encodes ${evidence.decoded_cid_from_place_id ?? "?"}`;
    case "non_venue_type":
      return `typed ${evidence.venues?.[0]?.place_type || "unknown"}`;
    case "venue_missing":
      return `no longer live, ${evidence.cache_rows ?? 0} cache rows still point at it`;
    case "cid_drift": {
      const current = evidence.current_cids?.length
        ? evidence.current_cids.join(", ")
        : "no CID on record";
      return `${evidence.cid ?? "an unknown CID"} no longer on this venue — it now holds ${current}`;
    }
    case "venue_named_by_address":
      return "the name is just the address — Google returned a place, not a business";
    case "duplicate_name_and_address":
      return `${evidence.venues?.length ?? 0} records, byte-identical name and street — merge candidate, not urgent`;
    case "duplicate_google_place":
      return "one venue's place id decodes to the other's primary CID — provably one Google place";
    case "place_not_venue": {
      // `?? 0` would read as an observed zero. The check skips a venue whose count could
      // not be taken, so an absent count on a rendered row means an older finding written
      // before the field existed — say so rather than assert "0 events ever".
      const count = evidence.mood_event_count;
      const events =
        count === undefined || count === null
          ? "event count not recorded"
          : count === 1
            ? "1 event ever"
            : `${count} events ever`;
      return `typed ${evidence.venues?.[0]?.place_type || "unknown"}  ·  ${events}`;
    }
    case "venue_twin": {
      // An absent count is an older finding, not an observed zero — same care as
      // `place_not_venue`'s event count above.
      const count = evidence.twin_event_pair_count;
      const pairs =
        count === undefined || count === null
          ? "duplicate event pairs not recorded"
          : `${count} duplicate event ${count === 1 ? "pair" : "pairs"}`;
      return `${pairs}  ·  ${metres(evidence.max_distance_m)}`;
    }
    default:
      return "";
  }
};

/** Where a curator opens one Mood event to compare, fix or delete it. */
export const venueAuditEventHref = (eventId: string): string =>
  `/events/edit/${eventId}`;

/** Reader-facing text for a `venue_twin` pair's match leg. */
export const VENUE_AUDIT_TWIN_MATCH_LABEL = {
  shared_artist: "shared artist",
  same_title: "same title",
} as const;

/**
 * `venue_named_by_address`'s own summary line: the name and the address are near-identical
 * by definition, so the default "name — address" pairing (`venueAuditVenueLine`) would
 * print the same string twice. Say the address once, then what is wrong with it.
 */
const summariseNamedByAddress = (row: VenueAuditFindingRow): string => {
  const venue = (row.evidence.venues ?? [])[0];
  const line = venue ? venueAuditVenueDisplayName(venue, row) : "";
  const parts = [line, venueAuditFindingMetric(row)].filter(
    (part) => part.length > 0,
  );
  return parts.length > 0 ? parts.join("  ·  ") : row.fingerprint;
};

/**
 * One line a curator can judge a row from without opening anything.
 *
 * Names every venue with its address, then the kind's own metric. Falls back to the
 * fingerprint when it has neither — an unknown kind with empty evidence still renders as
 * something identifiable rather than as an empty cell.
 */
export const summariseFinding = (row: VenueAuditFindingRow): string => {
  if (row.kind === "venue_named_by_address")
    return summariseNamedByAddress(row);
  const metric = venueAuditFindingMetric(row);
  const venues = venueAuditVenuesText(row);
  // `cid_drift` leads with what moved, not with the venue name: the venue is fine, only
  // the bind's evidence is stale, so naming it first would bury the actual finding.
  const parts = row.kind === "cid_drift" ? [metric, venues] : [venues, metric];
  const filtered = parts.filter((part) => part.length > 0);
  return filtered.length > 0 ? filtered.join("  ·  ") : row.fingerprint;
};
